import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Upload, ArrowLeft, FileSpreadsheet, ImagePlus, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const FIJI_ISLANDS = ["Viti Levu", "Vanua Levu", "Taveuni", "Kadavu", "Mamanuca Islands", "Yasawa Islands", "Beqa", "Ovalau", "Other"];
const ZONING = ["Residential", "Commercial", "Tourism", "Agricultural", "Industrial", "Mixed Use"];
const OWNERSHIP = [
    { id: "freehold", label: "Freehold" },
    { id: "native_lease", label: "Native Lease" },
    { id: "crown_lease", label: "Crown Lease" },
];
const PROPERTY_KIND = [
    { id: "land", label: "Bare land / lot" },
    { id: "residential_home", label: "Residential home" },
    { id: "apartment", label: "Apartment / condo" },
    { id: "commercial_building", label: "Commercial building" },
    { id: "mixed_use_building", label: "Mixed-use building" },
];

const blankRow = () => ({
    title: "",
    description: "",
    location_island: "Viti Levu",
    location_region: "",
    land_size_acres: "",
    zoning: "Residential",
    price_fjd: "",
    ownership_type: "freehold",
    property_kind: "land",
    bedrooms: "",
    bathrooms: "",
    floor_area_sqm: "",
    year_built: "",
    development_potential: "",
    listing_type: "sale",
    jv_eligible: false,
    external_photos: [""],
});

// CSV parser — handles quoted fields with commas, escaped quotes ""
function parseCSV(text) {
    const rows = [];
    let i = 0;
    while (i < text.length) {
        const row = [];
        let field = "";
        let inQuotes = false;
        while (i < text.length) {
            const c = text[i];
            if (inQuotes) {
                if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
                if (c === '"') { inQuotes = false; i++; continue; }
                field += c; i++;
            } else {
                if (c === '"') { inQuotes = true; i++; continue; }
                if (c === ",") { row.push(field); field = ""; i++; continue; }
                if (c === "\n" || c === "\r") {
                    row.push(field); field = "";
                    if (c === "\r" && text[i + 1] === "\n") i++;
                    i++;
                    if (row.length === 1 && row[0] === "") { row.length = 0; continue; }
                    break;
                }
                field += c; i++;
            }
        }
        if (field !== "" || row.length) { row.push(field); rows.push(row); }
    }
    return rows.filter((r) => r.some((c) => c && String(c).trim() !== ""));
}

const CSV_HEADERS = [
    "title", "description", "location_island", "location_region",
    "land_size_acres", "zoning", "price_fjd", "ownership_type",
    "property_kind", "bedrooms", "bathrooms", "floor_area_sqm", "year_built",
    "development_potential", "listing_type", "jv_eligible", "external_photo_url",
];
const CSV_SAMPLE = `${CSV_HEADERS.join(",")}\n` +
    `"5-acre beachfront","Sunset views","Viti Levu","Pacific Harbour",5,"Tourism",1500000,"native_lease","land",,,,,"Approved 12-villa eco-resort","both",true,"https://images.unsplash.com/photo-1540541338287-41700207dee6"\n` +
    `"3-bed Suva townhouse","Modern 2018 build","Viti Levu","Suva",0.15,"Residential",550000,"freehold","residential_home",3,2,180,2018,"Move-in ready","sale",false,"https://images.unsplash.com/photo-1564013799919-ab600027ffc6"`;

export default function BulkListings() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([blankRow()]);
    const [submitting, setSubmitting] = useState(false);
    const [dragOverRow, setDragOverRow] = useState(null);
    const fileRef = useRef(null);

    const addRow = () => setRows((r) => [...r, blankRow()]);
    const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
    const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

    const downloadCsvTemplate = () => {
        const blob = new Blob([CSV_SAMPLE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "bula-listings-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const importCsv = (file) => {
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const parsed = parseCSV(String(reader.result || ""));
                if (parsed.length < 2) { toast.error("CSV is empty or has no data rows."); return; }
                const headers = parsed[0].map((h) => String(h || "").trim().toLowerCase());
                const dataRows = parsed.slice(1);
                const idx = (key) => headers.indexOf(key);
                const newRows = dataRows.map((r) => {
                    const safe = (k) => {
                        const j = idx(k);
                        return j >= 0 ? String(r[j] ?? "").trim() : "";
                    };
                    const photoUrl = safe("external_photo_url") || safe("external_photos");
                    return {
                        title: safe("title"),
                        description: safe("description"),
                        location_island: safe("location_island") || "Viti Levu",
                        location_region: safe("location_region"),
                        land_size_acres: safe("land_size_acres"),
                        zoning: safe("zoning") || "Residential",
                        price_fjd: safe("price_fjd"),
                        ownership_type: safe("ownership_type") || "freehold",
                        property_kind: safe("property_kind") || "land",
                        bedrooms: safe("bedrooms"),
                        bathrooms: safe("bathrooms"),
                        floor_area_sqm: safe("floor_area_sqm"),
                        year_built: safe("year_built"),
                        development_potential: safe("development_potential"),
                        listing_type: safe("listing_type") || "sale",
                        jv_eligible: ["true", "1", "yes"].includes(safe("jv_eligible").toLowerCase()),
                        external_photos: photoUrl ? [photoUrl] : [""],
                    };
                }).filter((r) => r.title || r.price_fjd);
                if (newRows.length === 0) { toast.error("No valid rows found in CSV."); return; }
                if (newRows.length > 50) {
                    toast.warning(`CSV had ${newRows.length} rows; first 50 imported (max).`);
                    setRows(newRows.slice(0, 50));
                } else {
                    setRows(newRows);
                    toast.success(`${newRows.length} row${newRows.length === 1 ? "" : "s"} imported from CSV.`);
                }
            } catch (e) {
                toast.error("Could not parse CSV: " + (e?.message || "unknown error"));
            }
        };
        reader.onerror = () => toast.error("Could not read file.");
        reader.readAsText(file);
    };

    const handleCsvSelect = (e) => {
        const f = e.target.files?.[0];
        if (f) importCsv(f);
        e.target.value = "";  // allow re-upload of same file
    };

    const handlePhotoDrop = (e, rowIdx) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverRow(null);
        const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
        if (files.length === 0) { toast.warning("Drop image files only."); return; }
        // Read up to 5 files per row, store as data URLs in external_photos
        const maxToRead = Math.min(files.length, 5);
        const reads = files.slice(0, maxToRead).map((f) => new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result || ""));
            fr.onerror = reject;
            fr.readAsDataURL(f);
        }));
        Promise.all(reads).then((urls) => {
            updateRow(rowIdx, { external_photos: urls.filter(Boolean) });
            toast.success(`${urls.length} image${urls.length === 1 ? "" : "s"} attached to row ${rowIdx + 1}.`);
        }).catch(() => toast.error("Could not read dropped images."));
    };

    const submit = async () => {
        const payload = rows
            .map((r) => ({
                ...r,
                land_size_acres: parseFloat(r.land_size_acres) || 0,
                price_fjd: parseFloat(r.price_fjd) || 0,
                bedrooms: r.bedrooms ? parseInt(r.bedrooms, 10) : null,
                bathrooms: r.bathrooms ? parseFloat(r.bathrooms) : null,
                floor_area_sqm: r.floor_area_sqm ? parseFloat(r.floor_area_sqm) : null,
                year_built: r.year_built ? parseInt(r.year_built, 10) : null,
                external_photos: (r.external_photos || []).map((s) => String(s).trim()).filter(Boolean),
                jv_eligible: r.jv_eligible || ["jv", "both"].includes(r.listing_type),
            }))
            .filter((r) => r.title && r.description && r.price_fjd > 0 && r.land_size_acres > 0);

        if (payload.length === 0) {
            toast.error("At least one row needs title, description, price, and acres.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/listings/bulk", { listings: payload });
            const { created, skipped } = data.summary || {};
            if (created > 0 && skipped === 0) toast.success(`Uploaded ${created} listing${created === 1 ? "" : "s"}.`);
            else if (created > 0) toast.success(`Uploaded ${created}, skipped ${skipped}. Check details below.`);
            else toast.error(`All ${skipped} rows were skipped — review and try again.`);
            if (created > 0) navigate("/dashboard");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) return null;
    const isAgent = user.services?.includes("agent") || user.roles?.includes("agent");

    return (
        <div data-testid="bulk-listings-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-1">Bulk upload</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-2 text-foreground">
                        <FileSpreadsheet className="h-7 w-7 text-fiji-green" /> Add listings in bulk
                    </h1>
                    <p className="text-muted-foreground font-app mt-1 max-w-2xl">
                        Add up to 50 listings at once. Per-row JV-eligibility toggle, drag-and-drop photos, or import a CSV. {isAgent ? "As an agent, your verified status appears on every listing card." : ""}
                    </p>
                </div>
                <Badge className="bg-secondary text-foreground border border-border font-app font-medium px-3 py-1.5">
                    {rows.length} row{rows.length === 1 ? "" : "s"} · {rows.filter((r) => r.title && r.price_fjd && r.land_size_acres).length} ready
                </Badge>
            </div>

            {/* CSV import bar */}
            <Card className="p-4 mb-6 bg-secondary/30 dark:bg-card border-border" data-testid="csv-import-bar">
                <div className="flex items-center gap-3 flex-wrap">
                    <FileSpreadsheet className="h-5 w-5 text-fiji-green shrink-0" />
                    <div className="flex-1 min-w-[200px]">
                        <div className="font-app text-sm font-semibold text-foreground">Import from CSV</div>
                        <p className="font-app text-xs text-muted-foreground">
                            Up to 50 rows. Headers must match the template.
                        </p>
                    </div>
                    <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleCsvSelect} className="hidden" data-testid="csv-input" />
                    <Button variant="outline" onClick={() => fileRef.current?.click()} className="font-app" data-testid="import-csv-btn">
                        <Upload className="h-4 w-4 mr-2" /> Choose CSV
                    </Button>
                    <Button variant="ghost" onClick={downloadCsvTemplate} className="font-app text-muted-foreground" data-testid="download-csv-template-btn">
                        Download template
                    </Button>
                </div>
            </Card>

            <div className="space-y-4 mb-6" data-testid="bulk-rows">
                {rows.map((r, i) => (
                    <Card key={i} className="p-5 bg-card" data-testid={`bulk-row-${i}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-sm font-app font-semibold text-foreground">Listing #{i + 1}</div>
                            {rows.length > 1 && (
                                <Button onClick={() => removeRow(i)} variant="ghost" size="sm" className="text-destructive" data-testid={`remove-row-${i}`}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <Label>Title *</Label>
                                <Input data-testid={`row-${i}-title`} value={r.title} onChange={(e) => updateRow(i, { title: e.target.value })} placeholder="Beachfront 5-acre at Pacific Harbour" className="mt-1.5 bg-background text-foreground" />
                            </div>
                            <div>
                                <Label>Region</Label>
                                <Input data-testid={`row-${i}-region`} value={r.location_region} onChange={(e) => updateRow(i, { location_region: e.target.value })} placeholder="Pacific Harbour" className="mt-1.5 bg-background text-foreground" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Description *</Label>
                                <Textarea data-testid={`row-${i}-desc`} value={r.description} onChange={(e) => updateRow(i, { description: e.target.value })} placeholder="Sunset views, road frontage, ready for boutique resort." className="mt-1.5 bg-background text-foreground" rows={2} />
                            </div>
                            <div>
                                <Label>Property kind *</Label>
                                <Select value={r.property_kind} onValueChange={(v) => updateRow(i, { property_kind: v })}>
                                    <SelectTrigger className="mt-1.5 bg-background text-foreground" data-testid={`row-${i}-kind`}><SelectValue /></SelectTrigger>
                                    <SelectContent>{PROPERTY_KIND.map((x) => <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Island</Label>
                                <Select value={r.location_island} onValueChange={(v) => updateRow(i, { location_island: v })}>
                                    <SelectTrigger className="mt-1.5 bg-background text-foreground"><SelectValue /></SelectTrigger>
                                    <SelectContent>{FIJI_ISLANDS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Zoning</Label>
                                <Select value={r.zoning} onValueChange={(v) => updateRow(i, { zoning: v })}>
                                    <SelectTrigger className="mt-1.5 bg-background text-foreground"><SelectValue /></SelectTrigger>
                                    <SelectContent>{ZONING.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>{r.property_kind === "land" ? "Acres *" : "Lot acres"}</Label>
                                <Input data-testid={`row-${i}-acres`} type="number" step="0.01" value={r.land_size_acres} onChange={(e) => updateRow(i, { land_size_acres: e.target.value })} className="mt-1.5 bg-background text-foreground" />
                            </div>
                            <div>
                                <Label>Asking price (FJ$) *</Label>
                                <Input data-testid={`row-${i}-price`} type="number" step="1000" value={r.price_fjd} onChange={(e) => updateRow(i, { price_fjd: e.target.value })} className="mt-1.5 bg-background text-foreground" />
                            </div>
                            {/* Phase E — building-specific fields */}
                            {["residential_home", "apartment", "commercial_building", "mixed_use_building"].includes(r.property_kind) && (
                                <>
                                    <div>
                                        <Label>Floor area (sqm)</Label>
                                        <Input data-testid={`row-${i}-floor`} type="number" value={r.floor_area_sqm} onChange={(e) => updateRow(i, { floor_area_sqm: e.target.value })} className="mt-1.5 bg-background text-foreground" />
                                    </div>
                                    <div>
                                        <Label>Year built</Label>
                                        <Input data-testid={`row-${i}-year`} type="number" value={r.year_built} onChange={(e) => updateRow(i, { year_built: e.target.value })} placeholder="2018" className="mt-1.5 bg-background text-foreground" />
                                    </div>
                                </>
                            )}
                            {["residential_home", "apartment"].includes(r.property_kind) && (
                                <>
                                    <div>
                                        <Label>Bedrooms</Label>
                                        <Input data-testid={`row-${i}-bedrooms`} type="number" value={r.bedrooms} onChange={(e) => updateRow(i, { bedrooms: e.target.value })} className="mt-1.5 bg-background text-foreground" />
                                    </div>
                                    <div>
                                        <Label>Bathrooms</Label>
                                        <Input data-testid={`row-${i}-bathrooms`} type="number" step="0.5" value={r.bathrooms} onChange={(e) => updateRow(i, { bathrooms: e.target.value })} className="mt-1.5 bg-background text-foreground" />
                                    </div>
                                </>
                            )}
                            <div>
                                <Label>Ownership</Label>
                                <Select value={r.ownership_type} onValueChange={(v) => updateRow(i, { ownership_type: v })}>
                                    <SelectTrigger className="mt-1.5 bg-background text-foreground"><SelectValue /></SelectTrigger>
                                    <SelectContent>{OWNERSHIP.map((x) => <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Listing type</Label>
                                <Select value={r.listing_type} onValueChange={(v) => updateRow(i, { listing_type: v })}>
                                    <SelectTrigger className="mt-1.5 bg-background text-foreground"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="sale">Outright sale</SelectItem>
                                        <SelectItem value="jv">Joint Venture only</SelectItem>
                                        <SelectItem value="both">Open to either</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {/* Photos: drag-drop OR URL */}
                            <div className="md:col-span-2">
                                <Label>Photos · drop or paste URL</Label>
                                <div
                                    onDragEnter={(e) => { e.preventDefault(); setDragOverRow(i); }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDragLeave={() => setDragOverRow(null)}
                                    onDrop={(e) => handlePhotoDrop(e, i)}
                                    className={`mt-1.5 rounded-md border-2 border-dashed transition p-3 ${dragOverRow === i ? "bg-coral/10 border-coral" : "bg-background border-border"}`}
                                    data-testid={`row-${i}-dropzone`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <ImagePlus className="h-4 w-4 text-coral" />
                                        <span className="font-app text-xs text-muted-foreground">
                                            Drag-drop up to 5 images, or paste a URL below.
                                        </span>
                                    </div>
                                    <Input
                                        data-testid={`row-${i}-photo`}
                                        value={(r.external_photos?.[0] || "").startsWith("data:") ? "" : (r.external_photos?.[0] || "")}
                                        onChange={(e) => updateRow(i, { external_photos: [e.target.value] })}
                                        placeholder="https://images.unsplash.com/…"
                                        className="bg-card text-foreground"
                                    />
                                    {r.external_photos?.length > 0 && r.external_photos[0] && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {r.external_photos.slice(0, 5).map((src, j) => (
                                                <div key={j} className="relative h-16 w-16 rounded overflow-hidden bg-secondary border border-border">
                                                    {src && <img src={src} alt={`row${i}-photo${j}`} className="h-full w-full object-cover" />}
                                                    <button
                                                        type="button"
                                                        onClick={() => updateRow(i, { external_photos: r.external_photos.filter((_, k) => k !== j) })}
                                                        className="absolute top-0.5 right-0.5 bg-volcanic/80 hover:bg-volcanic text-white rounded-full p-0.5"
                                                        data-testid={`row-${i}-photo-remove-${j}`}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <Label>Development potential</Label>
                                <Input data-testid={`row-${i}-potential`} value={r.development_potential} onChange={(e) => updateRow(i, { development_potential: e.target.value })} placeholder="Approved for 12-villa eco-resort" className="mt-1.5 bg-background text-foreground" />
                            </div>
                            <label className="md:col-span-2 flex items-center gap-2 mt-2 cursor-pointer">
                                <Checkbox data-testid={`row-${i}-jv`} checked={r.jv_eligible} onCheckedChange={(v) => updateRow(i, { jv_eligible: !!v })} />
                                <span className="font-app text-sm text-foreground">Open to a Joint Venture on this listing</span>
                            </label>
                        </div>
                    </Card>
                ))}
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <Button variant="outline" onClick={addRow} data-testid="add-row-btn"><Plus className="h-4 w-4 mr-1" /> Add another listing</Button>
                <Button onClick={submit} disabled={submitting} data-testid="bulk-submit-btn" className="bg-ocean-green hover:bg-ocean text-white">
                    <Upload className="h-4 w-4 mr-1" /> {submitting ? "Uploading…" : `Upload ${rows.length} listing${rows.length === 1 ? "" : "s"}`}
                </Button>
            </div>
        </div>
    );
}
